import React, { useState } from 'react'
import ReactGA from 'react-ga4'
import { Dropdown, Message, Select } from 'semantic-ui-react'

import { apiUrl } from 'environment'

import RequestService from 'services/request.service'
import ToastService from 'services/toast.service'

import { HUMAN_GAME } from 'types/deck'

import { useActionless, useAppSelector } from 'redux/hooks'
import { TOGGLE_MODAL } from 'redux/active/actions/types'

import Modal from 'components/elements/Modal'
import PhatButton from 'components/formElements/PhatButton'

import { humanReadableObjectToDropdown, stringListToDropdown } from 'utils/DropdownConversions'

import styles from './addToCollection.module.scss'
import SimpleSpinner from 'components/elements/SimpleSpinner'

const addOptions = [
  {
    key: 1,
    value: 'Create',
    text: 'add to the quantity of that card in your collection as a new record',
  },
  {
    key: 2,
    value: 'Skip',
    text: 'skip over adding that card to your collection again',
  },
  {
    key: 3,
    value: 'Update',
    text: 'update the existing collection record if it is found',
  },
]

type Props = {}

const AddToCollectionModal = ({}: Props) => {
  const categories = useAppSelector(state => state.deck.categories)
  const cardMap = useAppSelector(state => state.deck.cardMap)
  const addToCollection = useAppSelector(state => state.active.modals.addToCollection)

  const [toggleModal] = useActionless(TOGGLE_MODAL)

  const [add, setAdd] = useState('Create')
  const [loading, setLoading] = useState(false)
  const [game, setGame] = useState(HUMAN_GAME.Paper)
  const [excludedCategories, setExcludedCategories] = useState(new Array<string>())

  const handleAddToCollection = () => {
    ReactGA.event({ category: 'Collection', action: 'Adding cards from a deck.' })

    setLoading(true)

    const cardsToAdd = Object.values(cardMap)
      .filter(card => {
        if (excludedCategories.includes(card.categories[0] || '')) return false
        return true
      })
      .map(card => ({
        card: card.cardId,
        quantity: card.qty,
        foil: card.modifier === 'Foil',
        modifier: card.modifier,
        language: 1,
        condition: 1,
        tags: [],
        addOption: add,
        game: game,
      }))

    return RequestService.post(apiUrl + '/api/collection/create/', cardsToAdd)
      .then(() => {
        ToastService.create('Cards added to your collection', 'Collection Service', 'success')
        toggleModal({ modalName: 'addToCollection', flag: false })
      })
      .catch(() => ToastService.create('Unable to add cards to your collection', 'Collection', 'error'))
      .finally(() => setLoading(false))
  }

  return (
    <Modal
      closeButton
      open={addToCollection}
      onClose={() => toggleModal({ modalName: 'addToCollection', flag: false })}
      modalClassName={styles.modal}>
      <h3>Add to your collection</h3>
      <div className={styles.content}>
        <Dropdown
          selection
          name="game"
          fluid
          options={humanReadableObjectToDropdown(HUMAN_GAME)}
          onChange={(_e: any, { value }: any) => setGame(value)}
          value={game}
        />
        <Select
          placeholder="Exclude Categories"
          name="categories"
          fluid
          multiple
          search
          selection
          options={stringListToDropdown(Object.keys(categories))}
          onChange={(_e: any, { value }: any) => setExcludedCategories(value)}
          value={excludedCategories}
        />
        <p>
          This will add all the cards in this deck to your personal collection. There is no way to undo this action
          other than manually removing the cards from your collection. Make sure to save if you've been editing as we
          will transfer the last saved version.
        </p>
        If we find a copy of a card already in your collection, we should&nbsp;
        <Dropdown
          inline
          options={addOptions}
          value={add}
          name="add"
          onChange={(_e: any, { value }: any) => setAdd(value)}
          style={{ color: 'green' }}
        />
      </div>

      <div className={styles.controls}>
        <PhatButton color="green" onClick={handleAddToCollection} disabled={loading}>
          {!loading ? (
            'Add to my collection'
          ) : (
            <>
              <SimpleSpinner size="xSmall" /> Adding cards...
            </>
          )}
        </PhatButton>
      </div>
    </Modal>
  )
}

export default AddToCollectionModal
